import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { AuthProviders } from './context/AuthProviders'
// ContentProvider stays at root because landing components consume CMS
// content via useContent() (10 published site_content rows: hero, features,
// footer, navbar, etc.). ChatContext was deleted on 2026-05-18 — v2 chat
// uses useVoiceFanOut + useChatAttachments instead, no shared provider.
import { ContentProvider } from './context/ContentContext'
import { CookieConsentProvider } from './context/CookieConsentContext'
import { ToastProvider } from './context/ToastContext'
import App from './App.jsx'
import { registerServiceWorker } from './lib/registerServiceWorker'
// Sentry is lazy-loaded on first capture (PERF-025) — no eager init at
// startup. The SDK (~55 KB gz) only enters the network when something
// needs to report AND VITE_SENTRY_DSN is set. captureException /
// setSentryUser / clearSentryUser remain fire-and-forget at call sites.

// Self-hosted fonts (PERF-014): replaces the Google Fonts link in
// index.html. Only the weights actually consumed by the design system
// are imported — Inter 400/500/600/700 (--font-body), Noto Serif
// 400/600/700/800 (--font-heading). Newsreader and Work Sans were
// dropped per the audit — Newsreader's italic-serif role falls back
// to Georgia (already in the font-family chain at every callsite);
// Work Sans was unused in src/. Net: -100-150 KB + 2 RTTs avoided
// per page load.
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/700.css'
import '@fontsource/noto-serif/400.css'
import '@fontsource/noto-serif/600.css'
import '@fontsource/noto-serif/700.css'
import '@fontsource/noto-serif/800.css'
// Noto Serif italic — picks up the role Newsreader used to play at lesson
// `em`, blockquote, certificate "editorial" line, quiz/exam explanations,
// and reflection prompts. One extra weight file vs dropping the italic
// entirely; still wins ~100 KB vs the original Newsreader 8-axis fetch.
import '@fontsource/noto-serif/400-italic.css'

import './index.css'

registerServiceWorker()

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <HelmetProvider>
      <BrowserRouter>
        <CookieConsentProvider>
          <AuthProviders>
            <ToastProvider>
              <ContentProvider>
                <App />
              </ContentProvider>
            </ToastProvider>
          </AuthProviders>
        </CookieConsentProvider>
      </BrowserRouter>
    </HelmetProvider>
  </StrictMode>
)
